import Vue from 'vue';
import Router, { RawLocation, Location, Route } from 'vue-router';
import qs from 'qs';

import { Dictionary } from '../types/Dictionary';
import { WmsPageComposer } from '../components/page-composer/page-composer';
import { DevIndexPage } from '../pages/dev-index/dev-index';
import { Language } from '../enums/Language';
import { ciGuard, CI_ROUTES, CI_URL } from './ci-redirects';
import { kpGuard, KP_ROUTES, KP_URL } from './kp-redirects';
import { reGuard, REDIRECT_ROUTES } from './redirects';

function stringifyQuery(params: Dictionary<string>) {
	return qs.stringify(params, { addQueryPrefix: true });
}

function parseQuery(query: string) {
	return qs.parse(query, { ignoreQueryPrefix: true });
}

function castQueryToString(location: Location) {
	if (!location.query) {
		return;
	}

	location.query = parseQuery(stringifyQuery(location.query));
}

// HACK: patch Router.push to cast query object to string values
// TODO remove when https://github.com/vuejs/vue-router/issues/2131 is resolved
Router.prototype.push = function(
	location: RawLocation,
	onComplete?: Function,
	onAbort?: Function
) {
	castQueryToString(location as Location);
	(this as any).history.push(location, onComplete, onAbort);
};

// HACK: patch Router.replace to cast query object to string values
// TODO remove when https://github.com/vuejs/vue-router/issues/2131 is resolved
Router.prototype.replace = function(
	location: RawLocation,
	onComplete?: Function,
	onAbort?: Function
) {
	castQueryToString(location as Location);
	(this as any).history.replace(location, onComplete, onAbort);
};

Vue.use(Router);

export const router = new Router({
	mode: 'history',
	routes: [
		{
			path: '/',
			name: 'homepage',
			component: WmsPageComposer,
			props: {
				pageUrl:
					window.location.href.indexOf(CI_URL) === -1
						? (window.location.href.indexOf(KP_URL) === -1 ? 'page-homepage' : 'komerce-uvod')
						: 'ci-home'
			}
		},
		{
			path: '/byt-detail/:apartmentCatalogNumber',
			name: 'apartment-detail',
			component: WmsPageComposer,
			props: {
				pageUrl: 'apartment-detail',
				pageTemplate: 'apartment-detail'
			}
		},
		{
			// redirect without apartment catalog number
			path: '/byt-detail',
			redirect: '/'
		},
		{
			path: '/byty-lokality',
			name: 'apartments-locations',
			component: WmsPageComposer,
			props: {
				pageUrl: 'apartments-locations',
				pageTemplate: 'apartments-locations'
			}
		},
		{
			path: '/prehled-bytu/:locationCatalogNumber',
			name: 'apartments-overview',
			component: WmsPageComposer,
			props: {
				pageUrl: 'apartments-overview',
				pageTemplate: 'apartments-overview'
			}
		},
		{
			// redirect without location catalog number
			path: '/prehled-bytu',
			redirect: '/'
		},
		{
			path: '/vyhledavani',
			name: 'apartment-search',
			component: WmsPageComposer,
			props: {
				pageUrl: 'apartment-search',
				pageTemplate: 'apartment-search'
			}
		},
		{
			path: '/lokalita/:locationCatalogNumber',
			name: 'apartment-location',
			component: WmsPageComposer,
			props: {
				pageUrl: 'apartment-location',
				pageTemplate: 'apartment-location'
			}
		},
		{
			// redirect without location catalog number
			path: '/lokalita',
			redirect: '/'
		},
		{
			path: '/homepage',
			redirect: '/'
		},
		...CI_ROUTES,
		...KP_ROUTES,
		...REDIRECT_ROUTES,
		{
			path: '/page/:pageUrl',
			name: 'custom-page',
			component: WmsPageComposer,
			props: (route: Route) => ({
				pageUrl: route.params.pageUrl
			})
		},
		{
			path: '/muj-vyber',
			name: 'favorite-products',
			component: WmsPageComposer,
			props: {
				pageUrl: 'favorite-products',
				pageTemplate: 'favorite-products'
			}
		},
		{
			path: '/dev-index',
			name: 'dev-index',
			component: DevIndexPage
		},
		{
			path: '*',
			beforeEnter(to, from, next) {
				const { afterClick } = (router as any).meta;

				// send all other route requets as HTTP GET requests
				if (afterClick) {
					window.location.href = to.fullPath;
				} else {
					window.location.href = `https://www.central-group.cz/nenalezeno.html`;
				}
			}
		}
	],
	stringifyQuery,
	parseQuery,
	scrollBehavior: () => {
		return { x: 0, y: 0 };
	}
});

// HACK: create place for navigation meta
(router as any).meta = {};

router.beforeEach((to, from, next) => {
	if (ciGuard(to, from, next)) {
		return;
	}

	if (kpGuard(to, from, next)) {
		return;
	}

	if (reGuard(to, from, next)) {
		return;
	}

	let langId = parseInt(to.query.jv) as Language;
	langId = langId === 2 ? 1 : langId;

	if (
		langId === Language.Ru &&
		!window.location.href.match(
			/(karlovy|nikolaika|vinogrady|central-group\.ru)/
		)
	) {
		window.location.href = window.location.href.replace(
			'central-group.cz',
			'central-group.ru'
		);
		return;
	}

	if (
		(langId === Language.Cz || langId === Language.En) &&
		window.location.href.match('central-group.ru')
	) {
		window.location.href = window.location.href.replace(
			'central-group.ru',
			'central-group.cz'
		);
		return;
	}

	next();
});

router.afterEach(to => {
	// HACK: reset navigation meta
	(router as any).meta = {};
});
